/*
 * The student's light / dark / auto choice (196).
 *
 * BEFORE THIS THERE WAS NO PREFERENCE AT ALL. App.js read prefers-color-scheme on mount and followed
 * every system change, and toggleTheme flipped the value in memory only -- so a manual choice was
 * undone by the next system change and always lost on reload. "Auto" is therefore not a new third
 * option; it is what the app has always silently done, and light/dark is what had no way to stick.
 *
 * A device preference, not a profile field: it belongs to the browser someone is reading on, and
 * user_profile has no column for it. Nothing here talks to the server.
 */

const STORAGE_KEY = 'birdsy.theme'

const THEME_AUTO = 'auto'
const THEME_LIGHT = 'light'
const THEME_DARK = 'dark'

const THEME_PREFERENCES = [THEME_LIGHT, THEME_DARK, THEME_AUTO]

const isValidPreference = (value) => THEME_PREFERENCES.includes(value)

/*
 * Every access is wrapped. localStorage does not merely return null when it is unavailable -- in a
 * privacy mode, or with site data blocked, reading it THROWS. A theme lookup must never be able to
 * stop the app from rendering, so a failure falls back to auto, which is the old behaviour exactly.
 */
const readThemePreference = () => {
	try {
		const stored = window.localStorage.getItem(STORAGE_KEY)
		return isValidPreference(stored) ? stored : THEME_AUTO
	}
	catch (err) {
		return THEME_AUTO
	}
}

const writeThemePreference = (preference) => {
	if (!isValidPreference(preference)) return
	try {
		window.localStorage.setItem(STORAGE_KEY, preference)
	}
	catch (err) {
		// the choice still applies for this session; it just will not outlive the tab
		console.warn('Could not save the theme preference', err)
	}
}

const systemPrefersDark = () => {
	try {
		return window.matchMedia('(prefers-color-scheme: dark)').matches
	}
	catch (err) {
		return false
	}
}

// the mode actually rendered: auto asks the system, anything else is the answer already
const resolveTheme = (preference) => {
	if (preference === THEME_LIGHT || preference === THEME_DARK) return preference
	return systemPrefersDark() ? THEME_DARK : THEME_LIGHT
}

export {
	STORAGE_KEY,
	THEME_AUTO,
	THEME_LIGHT,
	THEME_DARK,
	THEME_PREFERENCES,
	isValidPreference,
	readThemePreference,
	writeThemePreference,
	systemPrefersDark,
	resolveTheme,
}
